
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";

import {
  Box,
  Button,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { VisibilityRounded } from "@mui/icons-material";

import { Dialog, Table } from "../../layout";
import * as actions from "../../api/actions/pos";
import { AppContext } from "../../App";
import { controller, endpoints } from "../../api/actions/api";
import ViewReturnItems from "../purchase/return/ViewReturnItems";
import { safeOnComplete } from "../../utils/safeOnComplete";
import { formatCurrency } from "../../hooks/Currency";
import { formatDateTime } from "../../hooks/DateFormat";
import ReportPrintButton from "./ReportPrintButton";

const ReturnProd = (props) => {
  const { t } = useTranslation();
  const { _store, _getById } = props;
  const { setLoader } = useContext(AppContext);

  const reportRef = useRef(null);

  const [returns, setReturns] = useState([]);
  const [viewData, setViewData] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Period filters
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const invalidRange = Boolean(
    fromDate && toDate && fromDate > toDate
  );

  // Load returns
  useEffect(() => {
    setReturns([]);

    if (!_store?.id) return;

    setLoader(true);

    const onComplete = (res, err) => {
      safeOnComplete(
        (response) => {
          setReturns(
            response?.status === 200 &&
            Array.isArray(response.result)
              ? response.result
              : []
          );
        },
        (error) => {
          console.error("Failed to load returns:", error);
          setReturns([]);
        }
      )(res, err);

      setLoader(false);
    };

    _getById(
      endpoints.PurchReturn + controller.GetByStore,
      _store.id,
      actions.ActionType.List,
      onComplete
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_store?.id]);

  // Filter returns using returnDate
  const filteredReturns = useMemo(() => {
    if (invalidRange) return [];

    return returns.filter((item) => {
      if (!fromDate && !toDate) return true;

      if (!item.returnDate) return false;

      const date = new Date(item.returnDate);

      if (Number.isNaN(date.getTime())) {
        return false;
      }

      if (fromDate) {
        const from = new Date(
          `${fromDate}T00:00:00`
        );

        if (date < from) return false;
      }

      if (toDate) {
        const to = new Date(
          `${toDate}T23:59:59.999`
        );

        if (date > to) return false;
      }

      return true;
    });
  }, [returns, fromDate, toDate, invalidRange]);

  // Prepare table data for screen and print
  const tableData = useMemo(() => {
    return filteredReturns.map((item) => {
      const returnItems =
        item.purchReturnItems || [];

      const totalItem = returnItems.reduce(
        (sum, row) =>
          sum + Number(row.qty || 0),
        0
      );

      const totalPrice = returnItems.reduce(
        (sum, row) =>
          sum +
          Number(row.qty || 0) *
            Number(row.product?.purchPrice || 0),
        0
      );

      return {
        ...item,

        returnDate: formatDateTime(item.returnDate),

        supplier:
          item.purchase?.supplier?.company || "",

        totalItem,

        totalPrice: formatCurrency(
          totalPrice,
          _store
        ),

        returnUser: [
          item.user?.firstName,
          item.user?.lastName,
        ]
          .filter(Boolean)
          .join(" "),
      };
    });
  }, [filteredReturns, _store]);

  // Open original return data
  // Preserve the existing View Return behavior
  const handleView = (data) => {
    const originalReturn = returns.find(
      (item) => Number(item.id) === Number(data.id)
    );

    if (!originalReturn) return;

    setViewData(originalReturn);
    setShowForm(true);
  };

  const ActionComp = (data) => (
    <Box>
      <Tooltip arrow title={t("viewReturn")}>
        <IconButton
          onClick={() => handleView(data)}
          size="small"
          className="btn-primary mr-5"
        >
          <VisibilityRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  // Screen columns include View button
  const screenColumns = [
    {
      id: "actions",
      label: t("view"),
    },
    {
      id: "supplier",
      label: t("supplier"),
    },
    {
      id: "totalItem",
      label: t("totalItem"),
    },
    {
      id: "totalPrice",
      label: t("totalPrice"),
    },
    {
      id: "returnUser",
      label: t("returnBy"),
    },
    {
      id: "returnDate",
      label: t("date"),
    },
  ];

  // Print columns exclude View button
  const printColumns = [
    {
      id: "supplier",
      label: t("supplier"),
    },
    {
      id: "totalItem",
      label: t("totalItem"),
    },
    {
      id: "totalPrice",
      label: t("totalPrice"),
    },
    {
      id: "returnUser",
      label: t("returnBy"),
    },
    {
      id: "returnDate",
      label: t("date"),
    },
  ];

  return (
    <>
      {/* Date filters and Print Preview button */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ mr: 1 }}>
          {t("returns")}
        </Typography>

        <TextField
          label={t("fromDate")}
          type="date"
          size="small"
          value={fromDate}
          onChange={(e) =>
            setFromDate(e.target.value)
          }
          InputLabelProps={{
            shrink: true,
          }}
        />

        <TextField
          label={t("toDate")}
          type="date"
          size="small"
          value={toDate}
          onChange={(e) =>
            setToDate(e.target.value)
          }
          inputProps={{
            min: fromDate || undefined,
          }}
          InputLabelProps={{
            shrink: true,
          }}
          error={invalidRange}
          helperText={
            invalidRange
              ? "To Date must be on or after From Date"
              : ""
          }
        />

        <Button
          variant="outlined"
          onClick={() => {
            setFromDate("");
            setToDate("");
          }}
        >
          {t("clear")}
        </Button>

        <ReportPrintButton
          reportRef={reportRef}
          paperSize="Letter"
          reportTitle={t("returns")}
        />
      </Box>

      {/* Normal screen report */}
      {/* Search, Pagination and View remain enabled */}
      <Box
        sx={{
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 1,
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          {t("returns")}
        </Typography>

        {(fromDate || toDate) && (
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              textAlign: "center",
            }}
          >
            {t("fromDate")}: {fromDate || "All"}
            {"  —  "}
            {t("toDate")}: {toDate || "All"}
          </Typography>
        )}

        <Table
          ActionComp={ActionComp}
          HeaderComp={() => <div />}
          tblHead={screenColumns}
          tblData={tableData}
        />
      </Box>

      {/* Separate printable report */}
      <Box
        ref={reportRef}
        className="return-products-print"
        sx={{
          position: "absolute",
          left: "-10000px",
          top: 0,
          width: "1000px",
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        {/* Report title */}
        <Typography
          variant="h6"
          sx={{
            mb: 1,
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          {t("returns")}
        </Typography>

        {/* Selected date range */}
        {(fromDate || toDate) && (
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              textAlign: "center",
            }}
          >
            {t("fromDate")}: {fromDate || "All"}
            {"  —  "}
            {t("toDate")}: {toDate || "All"}
          </Typography>
        )}

        {/* All rows without Search, Pagination or View */}
        <Table
          printMode={true}
          ActionComp={() => <div />}
          HeaderComp={() => <div />}
          tblHead={printColumns}
          tblData={tableData}
        />
      </Box>

      {/* View Return Dialog */}
      {viewData && (
        <Dialog
          show={showForm}
          onClose={false}
          maxWidth="lg"
        >
          <ViewReturnItems
            setShowForm={setShowForm}
            viewData={viewData}
          />
        </Dialog>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  _store: state.pos.defStore,
});

const mapActionToProps = {
  _getById: actions.getById,
};

export default connect(
  mapStateToProps,
  mapActionToProps
)(ReturnProd);
